import * as React from 'react';
import { cn } from '../lib/utils';
import { pageUrl } from '../lib/campaign-styles';
import { Input } from './ui/input';
import { CopyButton } from './ui/copy-button';

interface PageLinkFieldProps {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string[];
}

export const PageLinkField = ({
  label,
  hint,
  value,
  onChange,
  required,
  error,
}: PageLinkFieldProps) => {
  const url = pageUrl(value);
  return (
    <div className="flex flex-col gap-1.5 sm:col-span-2">
      <span className="flex items-center gap-2 text-xs font-semibold text-ink">
        {label}
        {required && <span className="text-rose">*</span>}
      </span>
      <Input
        mono
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="5-best-cheap-stocks"
        className="font-mono text-xs"
      />
      {error && error.length > 0 && (
        <p className="text-sm text-destructive" role="alert">
          {error.join(', ')}
        </p>
      )}
      <div className="flex items-center gap-2 rounded-lg bg-paper px-3 py-2 hairline">
        <span className="shrink-0 font-mono text-xs font-semibold uppercase tracking-wide text-faint">
          Page link
        </span>
        {url ? (
          <a
            target="_blank"
            href={`${url}`}
            className="min-w-0 flex-1 truncate font-mono text-xs text-ink"
          >
            {url}
          </a>
        ) : (
          <span className="min-w-0 flex-0 truncate text-xs italic text-faint">
            Populates on save, once the slug is set
          </span>
        )}
        <div className="flex-1"></div>
        <CopyButton value={url} />
      </div>
      {hint && !error && <span className="text-xs leading-snug text-faint">{hint}</span>}
    </div>
  );
};

interface ShortcodesProps {
  codes: string[];
  onInsert: (code: string) => void;
  label?: string;
}

export const Shortcodes = ({
  codes,
  onInsert,
  label = 'Short codes — click to insert',
}: ShortcodesProps) => (
  <div className="rounded-lg bg-paper p-3 hairline">
    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-faint">{label}</div>
    <div className="flex flex-wrap gap-1.5">
      {codes.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onInsert?.(c)}
          className="rounded-md bg-surface px-2 py-1 font-mono text-xs text-accent hairline transition hover:bg-accent-soft"
        >
          {c}
        </button>
      ))}
    </div>
  </div>
);

interface RadioGroupProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
}

export const RadioGroup = ({ options, value, onChange }: RadioGroupProps) => (
  <div className="flex w-full rounded-lg bg-paper p-0.5 hairline">
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange?.(o)}
        className={cn(
          'flex-1 rounded-md px-3 py-1.5 text-center text-xs font-semibold transition',
          value === o ? 'bg-accent text-white shadow-sm' : 'text-muted hover:text-ink'
        )}
      >
        {o}
      </button>
    ))}
  </div>
);

interface SiteId {
  id: string;
  siteId: string;
  label: string;
}

interface SiteIdPickerProps {
  options: SiteId[];
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}

export const SiteIdPicker = ({ options, selected, onToggle, onClear }: SiteIdPickerProps) => {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const rootRef = React.useRef<HTMLDivElement>(null);

  // Close on outside pointerdown and Escape while open (same overlay
  // behavior as DateRangeField).
  React.useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const matches = React.useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) => o.siteId.toLowerCase().includes(needle) || o.label.toLowerCase().includes(needle)
    );
  }, [options, q]);

  return (
    <div ref={rootRef} className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {selected.length === 0 && (
          <span className="text-xs italic text-faint">No site IDs restricted</span>
        )}
        {selected.map((id) => {
          const site = options.find((o) => o.id === id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => onToggle(id)}
              title="Remove"
              className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-white transition hover:bg-accent-hover"
            >
              <span className="opacity-80">
                {site ? (
                  <>
                    <span className="font-mono font-semibold">{site.siteId}</span>
                    <span className="mx-1 text-faint/60">·</span>
                    {site.label}
                  </>
                ) : (
                  ''
                )}
              </span>
              <span className="text-white/70">×</span>
            </button>
          );
        })}
        {selected.length > 1 && (
          <button
            type="button"
            onClick={onClear}
            className="rounded-full px-2 py-1 text-xs font-medium text-muted underline-offset-2 transition hover:text-rose hover:underline"
          >
            clear all
          </button>
        )}
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex h-9 items-center gap-2 rounded-lg bg-surface px-3 text-xs font-semibold text-ink hairline transition hover:bg-line/40"
        >
          {open ? 'Close list' : 'Select site IDs'}
          <span className="font-mono text-xs text-faint">{options.length} available</span>
        </button>

        {open && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg bg-surface hairline shadow-lg">
            <div className="border-b border-line p-2">
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Type to search by site ID or name — e.g. 101 or Facebook"
                className="w-full rounded-md bg-paper px-3 py-2 text-xs text-ink outline-none placeholder:text-faint"
              />
            </div>
            <div className="max-h-56 overflow-y-auto">
              {matches.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-faint">No match</div>
              )}
              {matches.map((o) => {
                const on = selected.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => onToggle(o.id)}
                    className={cn(
                      'flex w-full items-center gap-3 border-b border-line/60 px-3 py-2 text-left text-xs transition last:border-b-0',
                      on ? 'bg-accent-soft/60 text-accent' : 'text-ink hover:bg-paper'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        on ? 'border-accent bg-accent text-white' : 'border-line-strong bg-surface'
                      )}
                    >
                      {on && (
                        <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none">
                          <path
                            d="M2.5 6.2 5 8.5 9.5 3.5"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-mono font-semibold">{o.siteId}</span>
                      <span className="mx-2 text-faint/60">·</span>
                      {o.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface PairRow {
  name: string;
  value: string;
}

interface PairRowsProps {
  rows: PairRow[];
  addLabel?: string;
  nameHint?: string;
  valueHint?: string;
  onChange?: (rows: PairRow[]) => void;
}

export const PairRows = ({
  rows,
  addLabel = 'Add row',
  nameHint = 'Name',
  valueHint = 'Value',
  onChange,
}: PairRowsProps) => {
  const [items, setItems] = React.useState(rows);
  const set = (i: number, k: keyof PairRow, v: string) =>
    setItems((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  React.useEffect(() => {
    onChange?.(items);
  }, [items, onChange]);

  const inputCls =
    'w-full rounded-md bg-surface px-2.5 py-1.5 font-mono text-xs text-ink hairline outline-none focus:shadow-[inset_0_0_0_2px_var(--color-accent)]';

  return (
    <div className="space-y-1.5">
      {items.map((r, i) => (
        <div key={i} className="flex gap-1.5">
          <input
            className={cn(inputCls, 'w-[38%]')}
            value={r.name}
            placeholder={nameHint}
            onChange={(e) => set(i, 'name', e.target.value)}
          />
          <input
            className={cn(inputCls, 'flex-1')}
            value={r.value}
            placeholder={valueHint}
            onChange={(e) => set(i, 'value', e.target.value)}
          />
          <button
            type="button"
            onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))}
            title="Remove"
            className="w-7 shrink-0 rounded-md text-[13px] text-faint transition hover:bg-line hover:text-rose"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setItems((xs) => [...xs, { name: '', value: '' }])}
        className="rounded-md border border-dashed border-line-strong px-2.5 py-1 text-xs font-medium text-muted transition hover:border-accent hover:text-accent"
      >
        + {addLabel}
      </button>
    </div>
  );
};
